/**
 * Phase 2E: CPT Reconciliation State Machine
 * 
 * Explicit state tracking for each authorized CPT.
 * Only surgeon confirmation can move CPT to CONFIRMED_PERFORMED.
 * No auto-promotion. No inference.
 */

export type CptReconciliationStatus = 
  | 'authorized'
  | 'confirmed_performed'
  | 'not_performed'
  | 'insufficient_documentation';

export type ConfirmationMethod = 'direct' | 'clarification_question' | 'demo_auto';

export type DocumentationConfidence = 'explicit' | 'partial' | 'ambiguous' | 'missing';

export interface CptReconciliationEntry {
  id: string;
  authorizationId: string;
  cptCode: string;
  cptDescription: string | null;
  
  // State machine
  status: CptReconciliationStatus;
  previousStatus: CptReconciliationStatus | null;
  
  // Documentation analysis
  documentationFound: boolean;
  documentationConfidence: DocumentationConfidence | null;
  matchedText: string | null;
  
  // Surgeon confirmation
  surgeonConfirmed: boolean;
  surgeonConfirmedAt: string | null;
  surgeonConfirmedBy: string | null;
  surgeonConfirmationMethod: ConfirmationMethod | null;
  
  // Billable determination
  isBillable: boolean;
  billableDeterminedAt: string | null;
  billableReason: string | null;
  
  // Audit
  isDemoEntry: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CptReconciliationAuditEntry {
  id: string;
  reconciliationEntryId: string;
  authorizationId: string;
  cptCode: string;
  action: string;
  previousStatus: CptReconciliationStatus | null;
  newStatus: CptReconciliationStatus | null;
  actorId: string | null;
  actorType: string;
  reason: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * State transition rules for CPT reconciliation
 */
export const VALID_TRANSITIONS: Record<CptReconciliationStatus, CptReconciliationStatus[]> = {
  authorized: ['confirmed_performed', 'not_performed', 'insufficient_documentation'],
  confirmed_performed: [], // Terminal state - no transitions allowed
  not_performed: [], // Terminal state - no transitions allowed
  insufficient_documentation: ['confirmed_performed', 'not_performed'], // Can be resolved
};

/**
 * Check if a status transition is valid
 */
export function isValidTransition(
  from: CptReconciliationStatus,
  to: CptReconciliationStatus
): boolean {
  return VALID_TRANSITIONS[from].includes(to);
}

/**
 * Determine if a CPT is billable based on its status
 * ONLY confirmed_performed CPTs are billable
 */
export function isCptBillable(status: CptReconciliationStatus): boolean {
  return status === 'confirmed_performed';
}

/**
 * Get status display information
 */
export function getStatusDisplay(status: CptReconciliationStatus): {
  label: string;
  variant: 'default' | 'success' | 'destructive' | 'secondary' | 'warning';
  description: string;
} {
  switch (status) {
    case 'authorized':
      return {
        label: 'Authorized',
        variant: 'default',
        description: 'Procedure was authorized, awaiting confirmation',
      };
    case 'confirmed_performed':
      return {
        label: 'Confirmed Performed',
        variant: 'success',
        description: 'Surgeon confirmed procedure was performed',
      };
    case 'not_performed':
      return {
        label: 'Not Performed',
        variant: 'destructive',
        description: 'Procedure was not performed during surgery',
      };
    case 'insufficient_documentation':
      return {
        label: 'Insufficient Documentation',
        variant: 'warning',
        description: 'Documentation does not support billing',
      };
  }
}

/**
 * Map database row to TypeScript interface
 */
export function mapDbRowToEntry(row: {
  id: string;
  authorization_id: string;
  cpt_code: string;
  cpt_description: string | null;
  status: string;
  previous_status: string | null;
  documentation_found: boolean;
  documentation_confidence: string | null;
  matched_text: string | null;
  surgeon_confirmed: boolean;
  surgeon_confirmed_at: string | null;
  surgeon_confirmed_by: string | null;
  surgeon_confirmation_method: string | null;
  is_billable: boolean;
  billable_determined_at: string | null;
  billable_reason: string | null;
  is_demo_entry: boolean;
  created_at: string;
  updated_at: string;
}): CptReconciliationEntry {
  return {
    id: row.id,
    authorizationId: row.authorization_id,
    cptCode: row.cpt_code,
    cptDescription: row.cpt_description,
    status: row.status as CptReconciliationStatus,
    previousStatus: row.previous_status as CptReconciliationStatus | null,
    documentationFound: row.documentation_found,
    documentationConfidence: row.documentation_confidence as DocumentationConfidence | null,
    matchedText: row.matched_text,
    surgeonConfirmed: row.surgeon_confirmed,
    surgeonConfirmedAt: row.surgeon_confirmed_at,
    surgeonConfirmedBy: row.surgeon_confirmed_by,
    surgeonConfirmationMethod: row.surgeon_confirmation_method as ConfirmationMethod | null,
    isBillable: row.is_billable,
    billableDeterminedAt: row.billable_determined_at,
    billableReason: row.billable_reason,
    isDemoEntry: row.is_demo_entry,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Map audit log database row to TypeScript interface
 */
export function mapDbRowToAuditEntry(row: {
  id: string;
  reconciliation_entry_id: string;
  authorization_id: string;
  cpt_code: string;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  actor_id: string | null;
  actor_type: string;
  reason: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}): CptReconciliationAuditEntry {
  return {
    id: row.id,
    reconciliationEntryId: row.reconciliation_entry_id,
    authorizationId: row.authorization_id,
    cptCode: row.cpt_code,
    action: row.action,
    previousStatus: row.previous_status as CptReconciliationStatus | null,
    newStatus: row.new_status as CptReconciliationStatus | null,
    actorId: row.actor_id,
    actorType: row.actor_type,
    reason: row.reason,
    details: row.details,
    createdAt: row.created_at,
  };
}

/**
 * Summary of reconciliation state for an authorization
 */
export interface ReconciliationSummary {
  totalCpts: number;
  confirmedPerformed: number;
  notPerformed: number;
  insufficientDocumentation: number;
  pendingConfirmation: number;
  billableCpts: string[];
  nonBillableCpts: string[];
  allConfirmed: boolean;
  canExport: boolean;
}

/**
 * Calculate reconciliation summary from entries
 */
export function calculateReconciliationSummary(
  entries: CptReconciliationEntry[]
): ReconciliationSummary {
  const confirmedPerformed = entries.filter(e => e.status === 'confirmed_performed');
  const notPerformed = entries.filter(e => e.status === 'not_performed');
  const insufficientDocumentation = entries.filter(e => e.status === 'insufficient_documentation');
  const pending = entries.filter(e => e.status === 'authorized');

  const billableCpts = confirmedPerformed.map(e => e.cptCode);
  const nonBillableCpts = entries
    .filter(e => e.status !== 'confirmed_performed')
    .map(e => e.cptCode);

  return {
    totalCpts: entries.length,
    confirmedPerformed: confirmedPerformed.length,
    notPerformed: notPerformed.length,
    insufficientDocumentation: insufficientDocumentation.length,
    pendingConfirmation: pending.length,
    billableCpts,
    nonBillableCpts,
    allConfirmed: pending.length === 0,
    canExport: pending.length === 0 && billableCpts.length > 0,
  };
}
